import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createPost, type CreatePostPayload } from '../../lib/services/feedService';
import { Spinner } from '../Spinner';
import { toast } from 'react-hot-toast';
import { PostRow } from '../../types/database';
import { Game } from '../../types';
import { supabase } from '../../lib/supabase'; // Importar supabase para Storage
import { v4 as uuidv4 } from 'uuid'; // Para gerar nomes de arquivo únicos
import { Image as ImageIcon, Link as LinkIcon, X, Search, Tag as TagIcon, CheckCircle, Layers, Puzzle, Palette, ShieldAlert } from 'lucide-react'; // Ícones
import { searchGames } from '../../lib/game/searchService'; // Importar busca de jogos
import useDebounce from '../../hooks/useDebounce'; // Importação padrão
import { getAvailableFilters, loadAvailableFilters } from '../../lib/game/filterService'; // <<< Importar funções de filtro

// TODO: Adicionar suporte para seleção de tags (Jogo, Mecânica, Categoria, Tema)
// TODO: Adicionar preview de imagem/vídeo
// TODO: Adicionar validação de URL do YouTube

// Helper para validar URL do YouTube
const YOUTUBE_REGEX = /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/|shorts\/)?)([\w\-]+)(\S+)?$/;

const isValidYouTubeUrl = (url: string): boolean => {
  if (!url) return true; // URL vazia é válida (opcional)
  return YOUTUBE_REGEX.test(url);
};

interface PostTagPayload {
  type: 'game' | 'category' | 'mechanic' | 'theme';
  value: string; // game_id para 'game', tag_name para os outros
  game_name?: string | null;
}

interface CreatePostFormProps {
  onPostCreated: (newPost: PostRow, tagsPayload: PostTagPayload[]) => void;
}

// Definir tipo explícito para o estado das tags disponíveis
interface AvailableTagsState {
  categories: string[];
  mechanics: string[];
  themes: string[];
}

// Valores padrão com tipo explícito
const DEFAULT_TAGS: AvailableTagsState = { categories: [], mechanics: [], themes: [] };

const CreatePostForm: React.FC<CreatePostFormProps> = ({ onPostCreated }) => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoUrlError, setVideoUrlError] = useState<string | null>(null); // Estado para erro de URL
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // Estado para upload
  const [isModeratingImage, setIsModeratingImage] = useState(false); // Renomeado de isModerating
  const [isModeratingText, setIsModeratingText] = useState(false); // Novo estado para moderação de TEXTO
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados para busca e seleção de jogo
  const [gameSearchQuery, setGameSearchQuery] = useState('');
  const [gameSearchResults, setGameSearchResults] = useState<Game[]>([]);
  const [isSearchingGames, setIsSearchingGames] = useState(false);
  const [selectedGameTag, setSelectedGameTag] = useState<Game | null>(null);
  const debouncedGameSearchQuery = useDebounce(gameSearchQuery, 400);

  // <<< NOVOS ESTADOS PARA TAGS >>>
  const [availableTags, setAvailableTags] = useState<AvailableTagsState>(DEFAULT_TAGS);
  const [loadingTags, setLoadingTags] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedMechanics, setSelectedMechanics] = useState<string[]>([]);
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [activeTagTab, setActiveTagTab] = useState<'category' | 'mechanic' | 'theme' | null>(null);

  // Estados para paginação das opções de tags no formulário
  const INITIAL_TAG_OPTIONS_LIMIT = 10; // Mostrar 10 inicialmente
  const [showAllCategoryOptions, setShowAllCategoryOptions] = useState(false);
  const [showAllMechanicOptions, setShowAllMechanicOptions] = useState(false);
  const [showAllThemeOptions, setShowAllThemeOptions] = useState(false);

  // <<< EFEITO PARA CARREGAR TAGS DISPONÍVEIS >>>
  useEffect(() => {
    const loadTags = async () => {
      setLoadingTags(true);
      try {
        // Tentar obter do cache primeiro
        const cached = getAvailableFilters(); // Assumindo que retorna { categories: string[], mechanics: string[], themes: string[] }
        if (cached.categories.length > 0 || cached.mechanics.length > 0 || cached.themes.length > 0) { // Verificar se cache não está vazio
          console.log("[CreatePostForm] Usando tags do cache:", cached);
          setAvailableTags({
            categories: cached.categories || [], // Garantir que seja array
            mechanics: cached.mechanics || [], // Garantir que seja array
            themes: cached.themes || [], // Garantir que seja array
          });
        } else {
          // Se não houver cache, carregar do serviço
          console.log("[CreatePostForm] Carregando tags do serviço...");
          const fresh = await loadAvailableFilters(); // Assumindo que retorna a mesma estrutura
          console.log("[CreatePostForm] Tags carregadas:", fresh);
          setAvailableTags({
            categories: fresh.categories || [], // Garantir que seja array
            mechanics: fresh.mechanics || [], // Garantir que seja array
            themes: fresh.themes || [], // Garantir que seja array
          });
        }
      } catch (err) {
        console.error("Erro ao carregar tags disponíveis:", err);
        toast.error('Não foi possível carregar as opções de tags.');
      } finally {
        setLoadingTags(false);
      }
    };
    loadTags();
  }, []);

  // <<< NOVAS FUNÇÕES PARA MANIPULAR SELEÇÃO DE TAGS >>>
  const toggleTagSelection = (type: 'category' | 'mechanic' | 'theme', value: string) => {
    switch (type) {
      case 'category':
        setSelectedCategories(prev => 
          prev.includes(value) ? prev.filter(c => c !== value) : [...prev, value]
        );
        break;
      case 'mechanic':
        setSelectedMechanics(prev =>
          prev.includes(value) ? prev.filter(m => m !== value) : [...prev, value]
        );
        break;
      case 'theme':
        setSelectedThemes(prev =>
          prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
        );
        break;
    }
  };

  // Função para limpar todas as tags não-jogo
  const clearNonGameTags = () => {
    setSelectedCategories([]);
    setSelectedMechanics([]);
    setSelectedThemes([]);
  };

  // Efeito para buscar jogos quando a query debounced mudar
  useEffect(() => {
    const performGameSearch = async () => {
      if (debouncedGameSearchQuery.length < 3) {
        setGameSearchResults([]);
        setIsSearchingGames(false);
        return;
      }
      setIsSearchingGames(true);
      try {
        const results = await searchGames({ searchText: debouncedGameSearchQuery }, 1, 5); // Busca simples, 5 resultados
        setGameSearchResults(results.data);
      } catch (err) {
        console.error("Erro ao buscar jogos para tag:", err);
        setGameSearchResults([]);
      } finally {
        setIsSearchingGames(false);
      }
    };

    performGameSearch();
  }, [debouncedGameSearchQuery]);

  const handleSelectGame = (game: Game) => {
    setSelectedGameTag(game);
    setGameSearchQuery(''); // Limpa busca
    setGameSearchResults([]); // Esconde resultados
  };

  const handleRemoveGameTag = () => {
    setSelectedGameTag(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo e tamanho (opcional)
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione um arquivo de imagem.');
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error('A imagem é muito grande. O tamanho máximo é 5MB.');
        return;
      }
      
      setImageFile(file);
      // Gerar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setImagePreview(null);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Limpar o input de arquivo
    }
  };

  // Função para fazer upload da imagem
  const handleImageUpload = async (): Promise<string | null> => {
    if (!imageFile || !user) return null;

    setIsUploading(true);
    setIsModeratingImage(false); // Resetar estado de moderação de IMAGEM
    setError(null); // Limpar erros anteriores
    const fileExt = imageFile.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    // Padronizar para user.id/uuid.ext para consistência
    const filePath = `${user.id}/${fileName}`; // Caminho no bucket 'post-images'

    try {
      console.log(`[CreatePostForm] Iniciando upload para: ${filePath}`);
      const { error: uploadError } = await supabase.storage
        .from('post-images')
        .upload(filePath, imageFile, {
          cacheControl: '3600', // Cache de 1 hora
          upsert: false // Não sobrescrever se já existir (improvável com uuid)
        });

      if (uploadError) {
        console.error('[CreatePostForm] Erro no upload Supabase:', uploadError);
        throw uploadError;
      }

      const { data: publicUrlData } = supabase.storage
        .from('post-images')
        .getPublicUrl(filePath);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        console.error('[CreatePostForm] Não foi possível obter URL pública após upload.');
        throw new Error('Não foi possível obter URL pública da imagem.');
      }
      const publicUrl = publicUrlData.publicUrl;
      console.log('[CreatePostForm] Imagem enviada, URL pública:', publicUrl);
      setIsUploading(false);

      // Etapa de Moderação de Imagem
      setIsModeratingImage(true);
      toast.loading('Verificando conteúdo da imagem...', { id: 'moderation-image-toast' });

      const { data: moderationResult, error: moderationError } = await supabase.functions.invoke('moderate-image', {
        body: { imageUrl: publicUrl, imagePath: filePath }
      });
      
      // --- Início da Simulação ---
      /*
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simula delay da API de moderação
      const moderationSimulatedResponse = { error: null, moderated: true, success: true, message: 'Conteúdo parece seguro (simulado).' }; 
      // Para simular falha: const moderationSimulatedResponse = { error: 'Conteúdo impróprio detectado (simulado).', moderated: true, success: false };
      const moderationResult = moderationSimulatedResponse;
      const moderationError = moderationSimulatedResponse.error ? new Error(moderationSimulatedResponse.error) : null;
      */
      // --- Fim da Simulação ---

      setIsModeratingImage(false);
      toast.dismiss('moderation-image-toast');

      // A Edge Function agora sempre retorna status 200.
      // O sucesso/falha da moderação é indicado no payload JSON.
      if (moderationError) {
        // Este bloco agora só deve ser atingido se houver um erro de rede fundamental
        // ou se a função supabase.functions.invoke falhar por um motivo inesperado ANTES da Edge Function responder.
        console.warn(`[CreatePostForm] Erro crítico ao invocar a função de moderação:`, moderationError);
        toast.error(moderationError.message || 'Falha crítica ao contatar o serviço de moderação.', { duration: 7000 });
        removeImage();
        return null;
      }

      // Verificar o payload da resposta da Edge Function
      if (moderationResult && !moderationResult.success) {
        // A função executou, mas indicou conteúdo impróprio ou falha interna na lógica da função.
        const detailedErrorMessage = moderationResult.error || 'Conteúdo da imagem considerado impróprio ou falha na verificação.';
        
        console.warn(`[CreatePostForm] Moderação falhou ou conteúdo impróprio: ${detailedErrorMessage}`);
        toast.error(detailedErrorMessage, { duration: 7000 });
        
        // A Edge Function já tratou da remoção da imagem no storage se for imprópria.
        // O frontend apenas limpa o preview e o estado do arquivo.
        console.log(`[CreatePostForm] A função 'moderate-image' indicou falha. Se o conteúdo foi impróprio, ela deveria ter removido ${filePath} do storage.`);
        removeImage(); // Limpar preview e file no frontend
        return null; // Indica que o upload/moderação falhou
      }

      // Se chegou aqui, a chamada à função foi bem-sucedida E moderationResult.success é true
      if (moderationResult && moderationResult.success) {
        toast.success(moderationResult.message || 'Imagem verificada e pronta!', { duration: 3000 });
      }
      
      return publicUrl;

    } catch (uploadError: any) {
      console.error('[CreatePostForm] Erro durante upload ou moderação de imagem:', uploadError);
      setError(`Erro no upload/moderação de imagem: ${uploadError.message}`);
      toast.error(`Falha no upload/moderação de imagem: ${uploadError.message}`);
      setIsUploading(false);
      setIsModeratingImage(false);
      toast.dismiss('moderation-image-toast');
      removeImage(); // Limpar se houve erro
      return null;
    }
  };

  // Função para validar URL ao mudar
  const handleVideoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setVideoUrl(newUrl);
    if (newUrl && !isValidYouTubeUrl(newUrl)) {
      setVideoUrlError('Por favor, insira uma URL válida do YouTube.');
    } else {
      setVideoUrlError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Permitir postar se tiver conteúdo, imagem, vídeo OU alguma tag selecionada
    const hasContent = content.trim() !== '';
    const hasMedia = !!imageFile || videoUrl.trim() !== '';
    const hasTags = selectedGameTag || selectedCategories.length > 0 || selectedMechanics.length > 0 || selectedThemes.length > 0;
    
    // <<< VALIDAÇÃO ADICIONADA AQUI >>>
    if (videoUrl && !isValidYouTubeUrl(videoUrl)) {
      setVideoUrlError('Por favor, corrija a URL do YouTube antes de publicar.');
      toast.error('URL do YouTube inválida.');
      return;
    }

    if (!user || (!hasContent && !hasMedia && !hasTags)) return;

    setIsSubmitting(true);
    setError(null);
    let imageUrlForPost: string | null = null;

    // 1. Moderação de Texto (se houver texto)
    if (hasContent) {
      setIsModeratingText(true);
      toast.loading('Verificando texto do post...', { id: 'moderation-text-toast' });
      try {
        const { data: textModerationResult, error: textModerationError } = await supabase.functions.invoke('moderate-text', {
          body: { textContent: content.trim() }
        });

        setIsModeratingText(false);
        toast.dismiss('moderation-text-toast');

        if (textModerationError) {
          console.warn('[CreatePostForm] Erro crítico ao invocar a função de moderação de texto:', textModerationError);
          toast.error(textModerationError.message || 'Falha crítica ao verificar o texto.', { duration: 7000 });
          setIsSubmitting(false);
          return;
        }

        if (textModerationResult && !textModerationResult.success) {
          const detailedErrorMessage = textModerationResult.error || 'Conteúdo do texto considerado impróprio.';
          console.warn(`[CreatePostForm] Moderação de texto falhou: ${detailedErrorMessage}`);
          toast.error(detailedErrorMessage, { duration: 7000 });
          setIsSubmitting(false);
          return; 
        }
        if (textModerationResult && textModerationResult.success) {
          toast.success(textModerationResult.message || 'Texto verificado!', { duration: 2000 });
        }
      } catch (err: any) {
        setIsModeratingText(false);
        toast.dismiss('moderation-text-toast');
        console.error('[CreatePostForm] Erro durante moderação de texto:', err);
        toast.error(`Falha ao verificar texto: ${err.message}`);
        setIsSubmitting(false);
        return;
      }
    }

    // 2. Upload e Moderação de Imagem (se houver imagem)
    if (imageFile) {
      imageUrlForPost = await handleImageUpload();
      if (!imageUrlForPost) {
        // handleImageUpload já mostra o toast e limpa o erro/estado
        setIsSubmitting(false);
        return;
      }
    }

    // 3. Criação do Post
    try {
      console.log('[CreatePostForm] Chamando createPost...');
      
      // Preparar tags para o payload - incluir todas as selecionadas
      const postTags: PostTagPayload[] = [];
      if (selectedGameTag) {
        postTags.push({ 
          type: 'game', 
          value: selectedGameTag.id, 
          game_name: selectedGameTag.name 
        }); 
      }
      selectedCategories.forEach(cat => postTags.push({ type: 'category', value: cat }));
      selectedMechanics.forEach(mech => postTags.push({ type: 'mechanic', value: mech }));
      selectedThemes.forEach(theme => postTags.push({ type: 'theme', value: theme }));
      
      const payload = {
        user_id: user.id,
        content_text: content.trim() || null,
        image_url: imageUrlForPost, 
        video_url: videoUrl.trim() || null,
        tags: postTags.map(({ type, value }) => ({ type, value })), 
      };
      const newPost = await createPost(payload);

      console.log('[CreatePostForm] Post criado com sucesso:', newPost);
      // Limpar o formulário completamente
      setContent('');
      setVideoUrl('');
      removeImage(); 
      handleRemoveGameTag(); 
      clearNonGameTags();
      setActiveTagTab(null);
      
      // Chamar onPostCreated com o PostRow retornado após a criação bem-sucedida
      if (newPost) {
        onPostCreated(newPost, postTags); 

        toast.success('Post publicado!');
      } else {
        // Isso não deveria acontecer se createPost não lançou erro, mas por segurança:
        setError('Falha ao obter dados do post recém-criado.');
        toast.error('Algo deu errado ao finalizar a publicação.');
      }

    } catch (err: any) {
      console.error("Erro ao criar post:", err);
      setError(err.message || 'Falha ao criar o post. Tente novamente.');
      toast.error('Falha ao criar o post.');
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
      setIsModeratingImage(false);
      setIsModeratingText(false);
    }
  };

  if (!user) {
    return null;
  }

  const canSubmit = user && 
                    (content.trim() !== '' || 
                     imageFile || 
                     videoUrl.trim() !== '' || 
                     selectedGameTag || 
                     selectedCategories.length > 0 || 
                     selectedMechanics.length > 0 || 
                     selectedThemes.length > 0) && 
                    !isSubmitting && !isUploading && !isModeratingImage && !isModeratingText;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 py-4 dark:bg-gray-800 dark:border-gray-700">
      <form onSubmit={handleSubmit} className="space-y-4 px-4">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={user ? `No que você está pensando, ${user.full_name || user.username}?` : 'Faça login para postar'}
          className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-blue-400 dark:focus:border-blue-400"
          rows={3}
          maxLength={1000}
          disabled={!user || isSubmitting}
        />

        {/* Input para URL do YouTube */}
        <div className="relative mb-2">
            <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18}/>
            <input
                type="url"
                value={videoUrl}
                onChange={handleVideoUrlChange}
                placeholder="Link do vídeo do YouTube (opcional)"
                className={`w-full p-2 pl-10 border rounded-md focus:outline-none focus:ring-2 resize-none ${videoUrlError ? 'border-red-500 ring-red-500 dark:border-red-400 dark:ring-red-400' : 'border-gray-300 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-blue-400 dark:focus:border-blue-400'}`}
                disabled={isSubmitting}
                aria-invalid={!!videoUrlError}
                aria-describedby={videoUrlError ? "video-url-error" : undefined}
            />
        </div>
        {/* Mensagem de erro para URL do vídeo */}
        {videoUrlError && (
          <p id="video-url-error" className="text-xs text-red-600 mt-1 dark:text-red-400">{videoUrlError}</p>
        )}

        {/* Preview da Imagem e Input de Arquivo */}
        {imagePreview && (
          <div className="mb-2 relative w-fit">
            <img src={imagePreview} alt="Preview" className="max-h-40 rounded-md object-contain border border-gray-300 dark:border-gray-600" />
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-2 -right-2 bg-gray-700 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none dark:bg-gray-600 dark:hover:bg-red-500"
              title="Remover imagem"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <label className={`flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 cursor-pointer dark:text-blue-400 dark:hover:text-blue-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <ImageIcon size={18} />
          <span>{imageFile ? 'Trocar imagem' : 'Adicionar imagem'}</span>
          <input 
            ref={fileInputRef}
            type="file" 
            accept="image/*" 
            onChange={handleImageChange} 
            className="hidden" 
            disabled={isSubmitting}
          />
        </label>

        {/* Seleção de TAGS (Categorias, Mecânicas, Temas) */}
        <div className="mt-4 mb-2 border-t pt-3 dark:border-gray-700">
          <div className="flex justify-between items-center mb-2">
             <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Adicionar Tags (Opcional):</span>
             {(selectedCategories.length > 0 || selectedMechanics.length > 0 || selectedThemes.length > 0) && (
               <button
                 type="button"
                 onClick={clearNonGameTags}
                 className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
               >
                 Limpar Tags
               </button>
             )}
          </div>
          
          {/* Abas para tipos de tag */}
          <div className="flex flex-wrap gap-1 border border-gray-200 rounded-md p-0.5 mb-2 w-fit dark:border-gray-600">
              {( [{'type': 'category', 'icon': Layers}, {'type': 'mechanic', 'icon': Puzzle}, {'type': 'theme', 'icon': Palette}] as const ).map(({ type, icon: Icon }) => (
                  <button
                    key={type}
                    type="button"
                    disabled={loadingTags}
                    className={`flex items-center py-1 px-3 rounded-md text-sm ${
                        activeTagTab === type
                        ? 'bg-white shadow text-indigo-700 dark:bg-gray-700 dark:text-indigo-300'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700'
                    } ${loadingTags ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => setActiveTagTab(prev => prev === type ? null : type)} // Toggle da aba
                  >
                    <Icon size={16} className="mr-1.5" />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                    {/* Contador de tags selecionadas por tipo */}
                    {type === 'category' && selectedCategories.length > 0 && <span className="ml-1.5 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full dark:bg-indigo-600/30 dark:text-indigo-300">{selectedCategories.length}</span>}
                    {type === 'mechanic' && selectedMechanics.length > 0 && <span className="ml-1.5 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full dark:bg-indigo-600/30 dark:text-indigo-300">{selectedMechanics.length}</span>}
                    {type === 'theme' && selectedThemes.length > 0 && <span className="ml-1.5 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full dark:bg-indigo-600/30 dark:text-indigo-300">{selectedThemes.length}</span>}
                  </button>
              ))}
          </div>
          
          {/* Conteúdo da aba ativa - Botões de seleção */}
          {activeTagTab && (
              <div className="p-2 border rounded-md dark:bg-gray-750 dark:border-gray-600">
                  {loadingTags ? (
                      <div className="text-center text-xs text-gray-500 py-2 dark:text-gray-400">Carregando opções...</div>
                  ) : (
                      <div className="flex flex-col gap-2"> {/* Alterado para flex-col para acomodar o botão "Ver mais" abaixo */} 
                          {activeTagTab === 'category' && (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {(showAllCategoryOptions ? availableTags.categories : availableTags.categories.slice(0, INITIAL_TAG_OPTIONS_LIMIT)).map(cat => (
                                    <button
                                        key={`cat-${cat}`}
                                        type="button"
                                        onClick={() => toggleTagSelection('category', cat)}
                                        className={`px-2.5 py-1 text-xs rounded-full border ${selectedCategories.includes(cat)
                                            ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-500 dark:text-indigo-100 dark:border-indigo-400'
                                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                              </div>
                              {availableTags.categories.length > INITIAL_TAG_OPTIONS_LIMIT && (
                                <button 
                                  type="button"
                                  onClick={() => setShowAllCategoryOptions(!showAllCategoryOptions)}
                                  className="mt-1 text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium self-start"
                                >
                                  {showAllCategoryOptions ? 'Ver menos categorias' : `Ver mais ${availableTags.categories.length - INITIAL_TAG_OPTIONS_LIMIT} categorias...`}
                                </button>
                              )}
                              {availableTags.categories.length === 0 && <span className="text-xs text-gray-500 italic dark:text-gray-400">Nenhuma categoria disponível.</span>}
                            </>
                          )}
                          
                          {activeTagTab === 'mechanic' && (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {(showAllMechanicOptions ? availableTags.mechanics : availableTags.mechanics.slice(0, INITIAL_TAG_OPTIONS_LIMIT)).map(mech => (
                                    <button
                                        key={`mech-${mech}`}
                                        type="button"
                                        onClick={() => toggleTagSelection('mechanic', mech)}
                                        className={`px-2.5 py-1 text-xs rounded-full border ${selectedMechanics.includes(mech)
                                          ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-500 dark:text-indigo-100 dark:border-indigo-400'
                                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        {mech}
                                    </button>
                                ))}
                              </div>
                              {availableTags.mechanics.length > INITIAL_TAG_OPTIONS_LIMIT && (
                                <button 
                                  type="button"
                                  onClick={() => setShowAllMechanicOptions(!showAllMechanicOptions)}
                                  className="mt-1 text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium self-start"
                                >
                                  {showAllMechanicOptions ? 'Ver menos mecânicas' : `Ver mais ${availableTags.mechanics.length - INITIAL_TAG_OPTIONS_LIMIT} mecânicas...`}
                                </button>
                              )}
                              {availableTags.mechanics.length === 0 && <span className="text-xs text-gray-500 italic dark:text-gray-400">Nenhuma mecânica disponível.</span>}
                            </>
                          )}

                          {activeTagTab === 'theme' && (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {(showAllThemeOptions ? availableTags.themes : availableTags.themes.slice(0, INITIAL_TAG_OPTIONS_LIMIT)).map(theme => (
                                    <button
                                        key={`theme-${theme}`}
                                        type="button"
                                        onClick={() => toggleTagSelection('theme', theme)}
                                        className={`px-2.5 py-1 text-xs rounded-full border ${selectedThemes.includes(theme)
                                          ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-500 dark:text-indigo-100 dark:border-indigo-400'
                                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        {theme}
                                    </button>
                                ))}
                              </div>
                              {availableTags.themes.length > INITIAL_TAG_OPTIONS_LIMIT && (
                                <button 
                                  type="button"
                                  onClick={() => setShowAllThemeOptions(!showAllThemeOptions)}
                                  className="mt-1 text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium self-start"
                                >
                                  {showAllThemeOptions ? 'Ver menos temas' : `Ver mais ${availableTags.themes.length - INITIAL_TAG_OPTIONS_LIMIT} temas...`}
                                </button>
                              )}
                              {availableTags.themes.length === 0 && <span className="text-xs text-gray-500 italic dark:text-gray-400">Nenhum tema disponível.</span>}
                            </>
                          )}
                      </div>
                  )}
              </div>
          )}
        </div>

        {/* Tag de Jogo Selecionada */}
        {selectedGameTag && (
          <div className="mb-2 p-2 border border-green-300 bg-green-50 rounded-md flex items-center justify-between dark:bg-green-800/30 dark:border-green-600">
            <div className="flex items-center text-sm">
              <TagIcon size={16} className="text-green-600 mr-2 dark:text-green-400" />
              <span className="font-medium text-green-800 dark:text-green-200">Jogo: {selectedGameTag.name}</span>
            </div>
            <button
              type="button"
              onClick={handleRemoveGameTag}
              className="text-gray-400 hover:text-red-600 p-0.5 rounded-full dark:text-gray-500 dark:hover:text-red-400"
              title="Remover tag de jogo"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Busca e Seleção de Jogo (só aparece se não houver jogo selecionado) */}
        {!selectedGameTag && (
            <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18}/>
                <input
                    type="text"
                    value={gameSearchQuery}
                    onChange={(e) => setGameSearchQuery(e.target.value)}
                    placeholder="Marcar um jogo (opcional)"
                    className="w-full p-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-blue-400 dark:focus:border-blue-400"
                    disabled={isSubmitting}
                />
                {(isSearchingGames || gameSearchResults.length > 0) && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto dark:bg-gray-700 dark:border-gray-600">
                        {isSearchingGames ? (
                            <div className="p-2 text-center text-gray-500 dark:text-gray-400">Buscando...</div>
                        ) : (
                            gameSearchResults.map(game => (
                                <button
                                    key={game.id}
                                    type="button"
                                    onClick={() => handleSelectGame(game)}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                                >
                                    {game.name} ({game.year})
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        )}

        {/* Mensagem de erro */}
        {error && (
          <p className="text-red-500 text-sm mt-2 dark:text-red-400">{error}</p>
        )}
        
        {isUploading && (
          <div className="flex items-center text-sm text-blue-600 dark:text-blue-400 mt-2">
            <Spinner size="small" />
            <span className="ml-2">Enviando imagem...</span>
          </div>
        )}
        {isModeratingImage && (
          <div className="flex items-center text-sm text-yellow-600 dark:text-yellow-400 mt-2">
            <ShieldAlert size={16} className="animate-pulse mr-2" />
            <span className="ml-1">Verificando conteúdo da imagem...</span>
          </div>
        )}
        {isModeratingText && (
          <div className="flex items-center text-sm text-orange-600 dark:text-orange-400 mt-2">
            <ShieldAlert size={16} className="animate-pulse mr-2" />
            <span className="ml-1">Analisando texto do post...</span>
          </div>
        )}

        {/* Botão de submissão */}
        <div className="flex justify-end mt-4">
          <button
            type="submit"
            disabled={!canSubmit}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-500"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <Spinner size="small" />
                <span className="ml-2">Publicando...</span>
              </span>
            ) : isUploading ? (
              <span className="flex items-center">
                <Spinner size="small" />
                <span className="ml-2">Enviando imagem...</span>
              </span>
            ) : isModeratingImage ? (
              <span className="flex items-center">
                <ShieldAlert size={16} className="animate-pulse mr-2" />
                <span className="ml-1">Verificando Imagem...</span>
              </span>
            ) : isModeratingText ? (
              <span className="flex items-center">
                <ShieldAlert size={16} className="animate-pulse mr-2" />
                <span className="ml-1">Analisando Texto...</span>
              </span>
            )
            : 'Publicar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePostForm; 